import app from 'flarum/forum/app';
import Extend from 'flarum/common/extenders';
import { extend } from 'flarum/common/extend';
import Link from 'flarum/common/components/Link';
import HeaderPrimary from 'flarum/forum/components/HeaderPrimary';
import DiscussionPage from 'flarum/forum/components/DiscussionPage';
import BlogPage from './components/BlogPage';
import ToolsPage from './components/ToolsPage';
function isOfwBlogDiscussion(discussion) {
  if (!discussion || typeof discussion.tags !== 'function') {
    return false;
  }
  const tags = discussion.tags() || [];
  return tags.some(tag => tag && typeof tag.slug === 'function' && tag.slug() === 'ofw-blog');
}
function getSecondaryTags(discussion) {
  if (!discussion || typeof discussion.tags !== 'function') {
    return [];
  }
  return (discussion.tags() || []).filter(tag => tag && typeof tag.slug === 'function' && tag.slug() !== 'ofw-blog');
}
function getArticleFeaturedImage(discussion) {
  const firstPost = discussion && typeof discussion.firstPost === 'function' ? discussion.firstPost() : null;
  if (!firstPost || typeof firstPost.contentHtml !== 'function' || !firstPost.contentHtml()) {
    return '/assets/pinoytalks-blog-og.webp';
  }
  const parsed = new DOMParser().parseFromString(firstPost.contentHtml(), 'text/html');
  const image = parsed.querySelector('img');
  return image ? image.getAttribute('src') : '/assets/pinoytalks-blog-og.webp';
}
async function loadRelatedArticles(discussion) {
  const currentId = discussion.id();
  const currentTags = new Set(getSecondaryTags(discussion).map(tag => tag.slug()));
  try {
    const articles = await app.store.find('discussions', {
      filter: {
        tag: 'ofw-blog'
      },
      include: 'firstPost,tags',
      page: {
        limit: 24,
        offset: 0
      }
    });
    const related = articles.filter(article => article.id() !== currentId).map(article => {
      const secondaryTags = getSecondaryTags(article);
      const score = secondaryTags.reduce((total, tag) => total + (currentTags.has(tag.slug()) ? 1 : 0), 0);
      return {
        article,
        secondaryTags,
        score
      };
    }).sort((a, b) => {
      if (b.score !== a.score) {
        return b.score - a.score;
      }
      return new Date(b.article.createdAt() || 0) - new Date(a.article.createdAt() || 0);
    }).slice(0, 3);
    if (!related.length) {
      return;
    }
    setTimeout(() => {
      document.querySelectorAll('.PinoyTalksRelatedArticles').forEach(element => element.remove());
      const firstPost = document.querySelector('.PostStream-item:first-child .Post');
      if (!firstPost) {
        return;
      }
      const section = document.createElement('section');
      section.className = 'PinoyTalksRelatedArticles';
      const heading = document.createElement('div');
      heading.className = 'PinoyTalksRelatedArticles-heading';
      const title = document.createElement('h2');
      title.textContent = 'Related Articles';
      const subtitle = document.createElement('p');
      subtitle.textContent = 'More practical guides for Filipino OFWs in Saudi Arabia.';
      heading.appendChild(title);
      heading.appendChild(subtitle);
      const grid = document.createElement('div');
      grid.className = 'PinoyTalksRelatedArticles-grid';
      related.forEach(_ref => {
        let article = _ref.article,
          secondaryTags = _ref.secondaryTags;
        const card = document.createElement('a');
        card.className = 'PinoyTalksRelatedArticleCard';
        card.href = app.route.discussion(article);
        const image = document.createElement('img');
        image.className = 'PinoyTalksRelatedArticleCard-image';
        image.src = getArticleFeaturedImage(article);
        image.alt = article.title();
        image.loading = 'lazy';
        const content = document.createElement('div');
        content.className = 'PinoyTalksRelatedArticleCard-content';
        if (secondaryTags.length) {
          const category = document.createElement('span');
          category.className = 'PinoyTalksRelatedArticleCard-category';
          category.textContent = secondaryTags[0].name();
          content.appendChild(category);
        }
        const articleTitle = document.createElement('h3');
        articleTitle.textContent = article.title();
        const createdAt = article.createdAt();
        if (createdAt) {
          const date = document.createElement('span');
          date.className = 'PinoyTalksRelatedArticleCard-date';
          date.textContent = new Date(createdAt).toLocaleDateString(undefined, {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
          });
          content.appendChild(articleTitle);
          content.appendChild(date);
        } else {
          content.appendChild(articleTitle);
        }
        card.appendChild(image);
        card.appendChild(content);
        grid.appendChild(card);
      });
      section.appendChild(heading);
      section.appendChild(grid);
      firstPost.appendChild(section);
    }, 50);
  } catch (error) {
    console.error('Could not load related OFW Blog articles:', error);
  }
}
let pinoyTalksReadingProgressCleanup = null;
function removeArticleReadingProgress() {
  if (typeof pinoyTalksReadingProgressCleanup === 'function') {
    pinoyTalksReadingProgressCleanup();
  }
  pinoyTalksReadingProgressCleanup = null;
  document.querySelectorAll('.PinoyTalksReadingProgress').forEach(element => element.remove());
}
function addArticleReadingProgress() {
  setTimeout(() => {
    removeArticleReadingProgress();
    const article = document.querySelector('.PostStream-item:first-child .Post-body');
    if (!article) {
      return;
    }
    const progress = document.createElement('div');
    progress.className = 'PinoyTalksReadingProgress';
    progress.setAttribute('role', 'progressbar');
    progress.setAttribute('aria-label', 'Article reading progress');
    progress.setAttribute('aria-valuemin', '0');
    progress.setAttribute('aria-valuemax', '100');
    progress.setAttribute('aria-valuenow', '0');
    const fill = document.createElement('div');
    fill.className = 'PinoyTalksReadingProgress-fill';
    progress.appendChild(fill);
    document.body.appendChild(progress);
    const updateProgress = () => {
      const articleTop = window.scrollY + article.getBoundingClientRect().top;
      const articleHeight = article.offsetHeight;
      const start = articleTop - 90;
      const end = articleTop + articleHeight - window.innerHeight + 90;
      let percentage = 0;
      if (end > start) {
        percentage = (window.scrollY - start) / (end - start) * 100;
      }
      percentage = Math.max(0, Math.min(100, percentage));
      fill.style.width = "".concat(percentage, "%");
      progress.setAttribute('aria-valuenow', Math.round(percentage).toString());
    };
    let progressFrame = null;
    const requestProgressUpdate = () => {
      if (progressFrame !== null) {
        return;
      }
      progressFrame = window.requestAnimationFrame(() => {
        progressFrame = null;
        updateProgress();
      });
    };
    window.addEventListener('scroll', requestProgressUpdate, {
      passive: true
    });
    window.addEventListener('resize', requestProgressUpdate);
    pinoyTalksReadingProgressCleanup = () => {
      window.removeEventListener('scroll', requestProgressUpdate);
      window.removeEventListener('resize', requestProgressUpdate);
      if (progressFrame !== null) {
        window.cancelAnimationFrame(progressFrame);
      }
      progress.remove();
    };
    updateProgress();
  }, 150);
}
function removeArticleAds() {
  document.querySelectorAll('.PinoyTalksArticleAd').forEach(element => element.remove());
}
function ensurePinoyTalksAdSenseLoader() {
  const existingLoader = document.querySelector('script[src*="pagead2.googlesyndication.com/pagead/js/adsbygoogle.js"]');
  if (existingLoader) {
    return;
  }
  const script = document.createElement('script');
  script.async = true;
  script.crossOrigin = 'anonymous';
  script.src = 'https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js' + '?client=ca-pub-5329779135859579';
  document.head.appendChild(script);
}
function createPinoyTalksArticleAd(slot, position) {
  const wrapper = document.createElement('div');
  wrapper.className = "PinoyTalksArticleAd PinoyTalksArticleAd--".concat(position);
  wrapper.innerHTML = "\n    <div class=\"PinoyTalksArticleAd-label\">\n      Advertisement\n    </div>\n\n    <ins\n      class=\"adsbygoogle\"\n      style=\"display:block\"\n      data-ad-client=\"ca-pub-5329779135859579\"\n      data-ad-slot=\"".concat(slot, "\"\n      data-ad-format=\"auto\"\n      data-full-width-responsive=\"true\"\n    ></ins>\n  ");
  return wrapper;
}
function initializePinoyTalksArticleAd(wrapper) {
  const ad = wrapper.querySelector('.adsbygoogle');
  if (!ad || ad.dataset.pinoytalksInitialized === 'true') {
    return;
  }
  ad.dataset.pinoytalksInitialized = 'true';
  try {
    window.adsbygoogle = window.adsbygoogle || [];
    window.adsbygoogle.push({});
  } catch (error) {
    console.error('PinoyTalks AdSense initialization failed:', error);
  }
}
function addArticleAds() {
  setTimeout(() => {
    removeArticleAds();
    ensurePinoyTalksAdSenseLoader();
    const postBody = document.querySelector('.PostStream-item:first-child .Post-body');
    if (!postBody) {
      return;
    }
    const paragraphs = Array.from(postBody.querySelectorAll(':scope > p'));
    if (paragraphs.length < 2) {
      console.warn('PinoyTalks Blog Ads: not enough article paragraphs found.');
      return;
    }

    // Mid-article ad.
    const middleIndex = Math.max(1, Math.floor(paragraphs.length / 2));
    const middleParagraph = paragraphs[Math.min(middleIndex, paragraphs.length - 1)];
    const midAd = createPinoyTalksArticleAd('7937750638', 'middle');
    middleParagraph.after(midAd);

    // Near-end ad.
    const nearEndAd = createPinoyTalksArticleAd('2685423950', 'near-end');
    const authorBox = postBody.querySelector('.PinoyTalksArticleAuthorBox');
    const shareBar = postBody.querySelector('.PinoyTalksArticleShareBar');
    if (authorBox) {
      authorBox.before(nearEndAd);
    } else if (shareBar) {
      shareBar.before(nearEndAd);
    } else {
      postBody.appendChild(nearEndAd);
    }
    const initializeAds = () => {
      initializePinoyTalksArticleAd(midAd);
      initializePinoyTalksArticleAd(nearEndAd);
    };
    const adsenseLoader = document.querySelector('script[src*="pagead2.googlesyndication.com/pagead/js/adsbygoogle.js"]');
    if (adsenseLoader) {
      setTimeout(initializeAds, 800);
    } else {
      setTimeout(initializeAds, 1500);
    }
  }, 350);
}
function addArticleTableOfContents() {
  setTimeout(() => {
    document.querySelectorAll('.PinoyTalksArticleTOC').forEach(element => element.remove());
    const postBody = document.querySelector('.PostStream-item:first-child .Post-body');
    if (!postBody) {
      return;
    }

    // Only collect article headings that are direct children of the post body.
    // This prevents headings inside the author box or other custom sections
    // from appearing in the Table of Contents.
    const headings = Array.from(postBody.children).filter(element => element.tagName === 'H2' || element.tagName === 'H3');
    if (headings.length < 2) {
      return;
    }
    const usedIds = new Set();
    headings.forEach((heading, index) => {
      let id = heading.id;
      if (!id) {
        id = heading.textContent.trim().toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
        if (!id) {
          id = "section-".concat(index + 1);
        }
      }
      let uniqueId = id;
      let counter = 2;
      while (usedIds.has(uniqueId)) {
        uniqueId = "".concat(id, "-").concat(counter);
        counter += 1;
      }
      usedIds.add(uniqueId);
      heading.id = uniqueId;
      heading.classList.add('PinoyTalksArticleAnchorHeading');
    });
    const toc = document.createElement('details');
    toc.className = 'PinoyTalksArticleTOC';
    if (window.innerWidth > 767) {
      toc.open = true;
    }
    const summary = document.createElement('summary');
    summary.className = 'PinoyTalksArticleTOC-summary';
    summary.innerHTML = "\n      <span>\n        <i class=\"fas fa-list-ul\" aria-hidden=\"true\"></i>\n        In this guide\n      </span>\n\n      <small>Jump to a section</small>\n    ";
    const list = document.createElement('ul');
    list.className = 'PinoyTalksArticleTOC-list';
    headings.forEach(heading => {
      const item = document.createElement('li');
      item.className = heading.tagName === 'H3' ? 'PinoyTalksArticleTOC-item PinoyTalksArticleTOC-item--sub' : 'PinoyTalksArticleTOC-item';
      const link = document.createElement('a');
      const targetId = heading.id;
      link.href = "#".concat(targetId);
      link.textContent = heading.textContent.trim();
      link.addEventListener('click', event => {
        event.preventDefault();
        const target = document.getElementById(targetId);
        if (!target) {
          return;
        }
        const headerOffset = window.innerWidth <= 767 ? 75 : 90;
        const targetPosition = target.getBoundingClientRect().top + window.pageYOffset - headerOffset;
        window.scrollTo({
          top: targetPosition,
          behavior: 'smooth'
        });

        // Update the URL without triggering Flarum navigation.
        window.history.replaceState(null, '', "#".concat(targetId));
      });
      item.appendChild(link);
      list.appendChild(item);
    });
    toc.appendChild(summary);
    toc.appendChild(list);

    // Place the TOC immediately before the article's first H2/H3 section.
    headings[0].before(toc);
  }, 50);
}
function addArticleAuthorBox() {
  setTimeout(() => {
    document.querySelectorAll('.PinoyTalksArticleAuthorBox').forEach(element => element.remove());
    const postBody = document.querySelector('.PostStream-item:first-child .Post-body');
    if (!postBody) {
      return;
    }
    const box = document.createElement('section');
    box.className = 'PinoyTalksArticleAuthorBox';
    box.innerHTML = "\n      <div class=\"PinoyTalksArticleAuthorBox-avatar\">\n        <i class=\"fas fa-newspaper\" aria-hidden=\"true\"></i>\n      </div>\n\n      <div class=\"PinoyTalksArticleAuthorBox-content\">\n        <span class=\"PinoyTalksArticleAuthorBox-label\">\n          About PinoyTalks\n        </span>\n\n        <h3>Practical information for Filipino OFWs in Saudi Arabia</h3>\n\n        <p>\n          PinoyTalks helps kababayang OFWs better understand topics involving\n          Qiwa, iqama, employment contracts, transfers, Saudi labor awareness,\n          Musaned, and everyday OFW concerns through practical educational\n          content and community discussions.\n        </p>\n\n        <div class=\"PinoyTalksArticleAuthorBox-actions\">\n          <a href=\"/blog\">\n            <i class=\"fas fa-book-open\" aria-hidden=\"true\"></i>\n            Explore OFW Guides\n          </a>\n\n          <a href=\"/t/ask-the-community\">\n            <i class=\"fas fa-users\" aria-hidden=\"true\"></i>\n            Ask the Community\n          </a>\n        </div>\n      </div>\n    ";
    postBody.appendChild(box);
  }, 100);
}
function addArticleShareBar(discussion) {
  setTimeout(() => {
    document.querySelectorAll('.PinoyTalksArticleShareBar').forEach(element => element.remove());

    /*
     * PinoyTalks: do not show global share bar on Souq.
     * Souq has its own private WhatsApp Seller contact.
     */
    const isSouq = discussion && typeof discussion.tags === 'function' && (discussion.tags() || []).some(tag => tag && typeof tag.slug === 'function' && tag.slug() === 'souq');
    if (isSouq) {
      return;
    }
    const postBody = document.querySelector('.PostStream-item:first-child .Post-body');
    if (!postBody) {
      return;
    }
    const articleUrl = new URL(app.route.discussion(discussion), window.location.origin).href;
    const articleTitle = discussion.title();
    const facebookUrl = 'https://www.facebook.com/sharer/sharer.php?u=' + encodeURIComponent(articleUrl);
    const whatsappUrl = 'https://wa.me/?text=' + encodeURIComponent(articleTitle + ' ' + articleUrl);
    const shareBar = document.createElement('section');
    shareBar.className = 'PinoyTalksArticleShareBar';
    shareBar.innerHTML = "\n      <div class=\"PinoyTalksArticleShareBar-heading\">\n        <span>Share this guide</span>\n        <small>Help another kabayan find this information.</small>\n      </div>\n\n      <div class=\"PinoyTalksArticleShareBar-buttons\">\n        <a\n          href=\"".concat(facebookUrl, "\"\n          target=\"_blank\"\n          rel=\"noopener noreferrer\"\n          class=\"PinoyTalksShareButton PinoyTalksShareButton--facebook\"\n        >\n          <i class=\"fab fa-facebook-f\" aria-hidden=\"true\"></i>\n          Facebook\n        </a>\n\n        <a\n          href=\"").concat(whatsappUrl, "\"\n          target=\"_blank\"\n          rel=\"noopener noreferrer\"\n          class=\"PinoyTalksShareButton PinoyTalksShareButton--whatsapp\"\n        >\n          <i class=\"fab fa-whatsapp\" aria-hidden=\"true\"></i>\n          WhatsApp\n        </a>\n\n        <button\n          type=\"button\"\n          class=\"PinoyTalksShareButton PinoyTalksShareButton--copy\"\n        >\n          <i class=\"fas fa-link\" aria-hidden=\"true\"></i>\n          <span>Copy Link</span>\n        </button>\n\n        <a\n          href=\"/blog\"\n          class=\"PinoyTalksShareButton PinoyTalksShareButton--back\"\n        >\n          <i class=\"fas fa-arrow-left\" aria-hidden=\"true\"></i>\n          Back to Blog\n        </a>\n      </div>\n    ");
    const copyButton = shareBar.querySelector('.PinoyTalksShareButton--copy');
    copyButton == null || copyButton.addEventListener('click', async () => {
      const label = copyButton.querySelector('span');
      try {
        await navigator.clipboard.writeText(articleUrl);
      } catch (error) {
        const textarea = document.createElement('textarea');
        textarea.value = articleUrl;
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        textarea.remove();
      }
      if (label) {
        label.textContent = 'Copied!';
        setTimeout(() => {
          label.textContent = 'Copy Link';
        }, 2000);
      }
    });
    postBody.appendChild(shareBar);
  }, 150);
}

// Blog article scroll-to-top button.
let articleScrollToTopHandler = null;
function addArticleScrollToTop() {
  setTimeout(() => {
    const appRoot = document.getElementById('app');
    if (!appRoot || !appRoot.classList.contains('App--pinoytalks-blog-article')) {
      return;
    }
    let button = document.querySelector('.PinoyTalksScrollToTop');
    if (!button) {
      button = document.createElement('button');
      button.type = 'button';
      button.className = 'PinoyTalksScrollToTop';
      button.setAttribute('aria-label', 'Scroll to top');
      button.setAttribute('title', 'Back to top');
      button.innerHTML = "\n        <i class=\"fas fa-arrow-up\" aria-hidden=\"true\"></i>\n      ";
      button.addEventListener('click', () => {
        window.scrollTo({
          top: 0,
          behavior: 'smooth'
        });
      });
      document.body.appendChild(button);
    }
    if (articleScrollToTopHandler) {
      window.removeEventListener('scroll', articleScrollToTopHandler);
    }
    articleScrollToTopHandler = () => {
      button.classList.toggle('is-visible', window.scrollY > 500);
    };
    window.addEventListener('scroll', articleScrollToTopHandler, {
      passive: true
    });
    articleScrollToTopHandler();
  }, 100);
}
function removeArticleScrollToTop() {
  if (articleScrollToTopHandler) {
    window.removeEventListener('scroll', articleScrollToTopHandler);
    articleScrollToTopHandler = null;
  }
  document.querySelectorAll('.PinoyTalksScrollToTop').forEach(button => button.remove());
}

// Keep wide article tables inside the mobile viewport.
function addArticleTableScroll() {
  setTimeout(() => {
    document.querySelectorAll('.App--pinoytalks-blog-article .Post-body table').forEach(table => {
      var _table$parentElement;
      if ((_table$parentElement = table.parentElement) != null && _table$parentElement.classList.contains('PinoyTalksTableScroll')) {
        return;
      }
      const wrapper = document.createElement('div');
      wrapper.className = 'PinoyTalksTableScroll';
      table.parentNode.insertBefore(wrapper, table);
      wrapper.appendChild(table);
    });
  }, 150);
}

// PinoyTalks Souq navigation link.
extend(HeaderPrimary.prototype, 'items', function (items) {
  items.add('pinoytalks-souq-nav', m(Link, {
    href: "/souq",
    className: "PinoyTalksSouqNavLink"
  }, m("i", {
    className: "fas fa-store",
    "aria-hidden": "true"
  }), m("span", null, "Souq")), 21);
});

// PinoyTalks Tools navigation link.
extend(HeaderPrimary.prototype, 'items', function (items) {
  items.add('pinoytalks-tools', m(Link, {
    href: app.route('pinoytalks.tools'),
    className: "PinoyTalksToolsNavLink"
  }, m("i", {
    className: "fas fa-layer-group",
    "aria-hidden": "true"
  }), m("span", null, "Tools")), 19);
});
// PinoyTalks Ask the Community navigation link.
extend(HeaderPrimary.prototype, 'items', function (items) {
  items.add('pinoytalks-community', m(Link, {
    href: "/t/ask-the-community",
    className: "PinoyTalksCommunityNavLink"
  }, m("i", {
    className: "fas fa-users",
    "aria-hidden": "true"
  }), m("span", null, "Ask the Community")), 18);
});
extend(HeaderPrimary.prototype, 'items', function (items) {
  items.add('pinoytalks-blog', m(Link, {
    href: app.route('pinoytalks.blog'),
    className: "PinoyTalksBlogNavLink"
  }, m("i", {
    className: "fas fa-newspaper",
    "aria-hidden": "true"
  }), m("span", null, "Blog")), 20);
});

// Mark only OFW Blog discussions with a dedicated article-layout class.
extend(DiscussionPage.prototype, 'show', function (_returned, discussion) {
  const appRoot = document.getElementById('app');
  if (!appRoot) {
    return;
  }
  const isBlogArticle = isOfwBlogDiscussion(discussion);
  appRoot.classList.toggle('App--pinoytalks-blog-article', isBlogArticle);
  if (!isBlogArticle) {
    removeArticleAds();
    removeArticleScrollToTop();
    document.querySelectorAll('.PinoyTalksRelatedArticles').forEach(element => element.remove());
    return;
  }
  addArticleReadingProgress();
  addArticleTableOfContents();
  addArticleTableScroll();
  addArticleAds();
  addArticleAuthorBox();
  addArticleScrollToTop();
  addArticleShareBar(discussion);
  loadRelatedArticles(discussion);
  setTimeout(() => {
    const hero = document.querySelector('.DiscussionHero .container');
    if (!hero || hero.querySelector('.PinoyTalksArticleMetaBar')) {
      return;
    }
    const firstPostBody = document.querySelector('.PostStream-item:first-child .Post-body');
    let readingTime = 1;
    if (firstPostBody) {
      const words = firstPostBody.innerText.trim().split(/\s+/).filter(Boolean).length;
      readingTime = Math.max(1, Math.ceil(words / 220));
    }
    const metaBar = document.createElement('div');
    metaBar.className = 'PinoyTalksArticleMetaBar';
    metaBar.innerHTML = "\n      <a href=\"/blog\" class=\"PinoyTalksArticleBackLink\">\n        <i class=\"fas fa-arrow-left\"></i>\n        Back to OFW Blog\n      </a>\n\n      <span class=\"PinoyTalksArticleReadingTime\">\n        <i class=\"far fa-clock\"></i>\n        ".concat(readingTime, " min read\n      </span>\n    ");
    hero.prepend(metaBar);
  }, 100);
});

// Always clean up the class when leaving a discussion page.
extend(DiscussionPage.prototype, 'onremove', function () {
  var _document$getElementB;
  removeArticleAds();
  removeArticleReadingProgress();
  removeArticleScrollToTop();
  (_document$getElementB = document.getElementById('app')) == null || _document$getElementB.classList.remove('App--pinoytalks-blog-article');
});
export default [new Extend.Routes().add('pinoytalks.blog', '/blog', BlogPage).add('pinoytalks.tools', '/tools', ToolsPage)];
/* PinoyTalks signup subtitle */
extend('flarum/forum/components/SignUpModal', 'body', function (returned) {
  if (!Array.isArray(returned)) return;
  returned.unshift(m("p", {
    className: "PinoyTalksSignUpSubtitle"
  }, "Create your free account and connect with fellow OFWs."));
});
/* ========================================
   PINOYTALKS PWA LAUNCH ANIMATION
   Runs only in installed PWA mode
   ======================================== */

app.initializers.add('pinoytalks-pwa-launch-animation', () => {
  const isStandalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;
  if (!isStandalone) {
    return;
  }
  function closeDesktopPane() {
    if (!window.matchMedia('(min-width: 992px)').matches) return;
    const pane = app.pane;
    if (!pane) return;
    if (pane.pinned) {
      pane.togglePinned();
    }
    pane.hide();
  }
  app.initializers.add('pinoytalks-force-close-discussion-pane', () => {
    extend('flarum/forum/components/DiscussionPage', 'oncreate', function () {
      requestAnimationFrame(() => {
        closeDesktopPane();
      });
    });
    extend('flarum/forum/components/DiscussionPage', 'onupdate', function () {
      requestAnimationFrame(() => {
        closeDesktopPane();
      });
    });
  });

  // Show only once during the current PWA session.
  if (sessionStorage.getItem('pinoytalksPwaLaunchShownV2')) {
    return;
  }
  sessionStorage.setItem('pinoytalksPwaLaunchShownV2', '1');
  const splash = document.createElement('div');
  splash.className = 'PinoyTalksPwaLaunch';
  splash.innerHTML = "\n    <div class=\"PinoyTalksPwaLaunch-inner\">\n      <div class=\"PinoyTalksPwaLaunch-ring\"></div>\n\n      <img\n        class=\"PinoyTalksPwaLaunch-logo\"\n        src=\"/assets/pinoytalks-icon-192.png\"\n        alt=\"PinoyTalks\"\n      >\n\n      <div class=\"PinoyTalksPwaLaunch-name\">\n        PinoyTalks\n      </div>\n\n      <div class=\"PinoyTalksPwaLaunch-tagline\">\n        Para sa OFW. Gawa ng OFW.\n      </div>\n    </div>\n  ";
  document.body.appendChild(splash);
  requestAnimationFrame(() => {
    splash.classList.add('is-active');
  });
  setTimeout(() => {
    splash.classList.add('is-leaving');
  }, 2600);
  setTimeout(() => {
    splash.remove();
  }, 3200);
});